// UserTrackerService.js
import { LambdaClient, InvokeCommand } from "@aws-sdk/client-lambda";
import { Auth } from "aws-amplify";
import config from "../aws-exports";
import { v4 as uuidv4 } from "uuid";

const getBackendName = () => {
  const backend = config.aws_user_files_s3_bucket.split("-");
  return backend[backend.length - 1];
};

// Cache IP address for the entire session (until page reload)
let cachedIP = null;
let ipFetchPromise = null;

/**
 * Get user's IP address from a simple API
 * Fetches once per session and caches the result
 */
const getUserIP = async () => {
  // Return cached IP if already fetched
  if (cachedIP !== null) {
    return cachedIP;
  }

  // If fetch is in progress, wait for it
  if (ipFetchPromise) {
    return ipFetchPromise;
  }

  // Start new fetch
  ipFetchPromise = (async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000); // 2 second timeout

      const response = await fetch("https://api.ipify.org?format=json", {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const data = await response.json();
      cachedIP = data.ip || null;

      if (process.env.NODE_ENV === "development") {
        console.log("IP fetched and cached:", cachedIP);
      }

      return cachedIP;
    } catch (error) {
      // Silent fail - IP is optional
      if (process.env.NODE_ENV === "development") {
        console.warn("Failed to fetch IP:", error.message);
      }
      cachedIP = null;
      return null;
    } finally {
      ipFetchPromise = null;
    }
  })();

  return ipFetchPromise;
};

/**
 * Track user via external PHP endpoint (for guest users)
 * @param {Object} trackingData - Tracking data object
 */
const trackViaExternalAPI = async (trackingData) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

    const response = await fetch(
      "https://grains.global/post_user_tracker.php",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer AMTPL2025smTKWbApp",
        },
        body: JSON.stringify(trackingData),
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);

    if (response.ok) {
      const result = await response.json();
      if (process.env.NODE_ENV === "development") {
        console.log("Guest user tracking recorded (external API):", result);
      }
      return true;
    } else {
      throw new Error(`HTTP ${response.status}`);
    }
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn("External API tracking failed:", error.message);
    }
    return false;
  }
};

/**
 * Track user via Lambda (for authenticated users)
 * @param {Object} trackingData - Tracking data object
 * @param {string} accessToken - User's JWT token
 */
const trackViaLambda = async (trackingData, accessToken) => {
  try {
    const credentials = await Auth.currentCredentials();
    const lambdaClient = new LambdaClient({
      credentials: Auth.essentialCredentials(credentials),
      region: config.aws_project_region,
    });

    const body = {
      target: "createUserTracker",
      userTrackerInput: trackingData,
      access_token: accessToken,
    };

    const params = {
      FunctionName: `dataops-${getBackendName()}`,
      Payload: JSON.stringify({ body }),
    };

    const command = new InvokeCommand(params);
    await lambdaClient.send(command);

    if (process.env.NODE_ENV === "development") {
      console.log("Authenticated user tracking recorded (Lambda):", {
        page: trackingData.Page,
        email: trackingData.Email,
      });
    }
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn("Lambda tracking failed:", error.message);
    }
    return false;
  }
};

/**
 * Track user page visit - Non-blocking fire-and-forget
 * @param {Object} trackingData - { page, service, reference }
 *
 * Uses external PHP API for guest users and Lambda for authenticated users
 */
export const trackUserPage = async (trackingData = {}) => {
  // Fire and forget - don't await or block
  Promise.resolve().then(async () => {
    try {
      // Check if user is authenticated
      let userEmail = null;
      let accessToken = null;
      let isAuthenticated = false;

      try {
        const user = await Auth.currentAuthenticatedUser({
          bypassCache: false,
        });
        userEmail = user?.attributes?.email || null;
        accessToken = user?.signInUserSession?.idToken?.jwtToken || null;
        isAuthenticated = !!accessToken;
      } catch (authError) {
        // User not authenticated
        isAuthenticated = false;
      }

      // Get IP address (cached after first fetch)
      const userIP = await getUserIP();

      // Get user agent
      const userAgent = navigator.userAgent || "Unknown";

      // Prepare tracking data
      const userTrackerInput = {
        Id: uuidv4(),
        Email: userEmail || "guest@unknown.com",
        IP: userIP || "Unknown",
        UserAgent: userAgent,
        Page: trackingData.page || window.location.pathname,
        Service: trackingData.service || null,
        Reference: trackingData.reference || null,
      };

      // Check if we're in production environment
      const isProduction =
        process.env.REACT_APP_AMPLIFY_ENV === "prod" ||
        process.env.REACT_APP_AMPLIFY_ENV === "production";

      // Route to appropriate tracking method
      if (isAuthenticated) {
        // Use Lambda for authenticated users
        await trackViaLambda(userTrackerInput, accessToken);
      } else if (isProduction) {
        // Use external PHP API for guest users in production only
        await trackViaExternalAPI(userTrackerInput);
      } else {
        // Skip tracking for guest users in development
        if (process.env.NODE_ENV === "development") {
          console.log(
            "Guest user tracking skipped (development environment):",
            userTrackerInput
          );
        }
      }
    } catch (error) {
      // Silently fail - don't disrupt user experience
      if (process.env.NODE_ENV === "development") {
        console.warn("User tracking failed:", error);
      }
    }
  });
};

/**
 * Track user action with additional context
 * @param {string} service - Service name (e.g., 'requirement', 'offer', 'trade')
 * @param {string} reference - Reference ID or additional context
 */
export const trackUserAction = (service, reference = null) => {
  trackUserPage({
    page: window.location.pathname,
    service,
    reference,
  });
};

/**
 * Initialize user tracking on app load
 */
export const initUserTracking = () => {
  // Track initial page load
  trackUserPage();

  // Track page changes (for SPA navigation)
  let lastPath = window.location.pathname;
  const observer = new MutationObserver(() => {
    const currentPath = window.location.pathname;
    if (currentPath !== lastPath) {
      lastPath = currentPath;
      trackUserPage();
    }
  });

  // Observe URL changes
  observer.observe(document.querySelector("body"), {
    childList: true,
    subtree: true,
  });

  return () => observer.disconnect();
};
